import React, { FC, useState, useEffect } from "react";
import { Table, Select, InputNumber, Space, Empty, DatePicker } from "antd";
import Icon, {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Alert, Button, Spinner } from "reactstrap";
import { ReactTyped } from "react-typed";
import {
  MaterialOrService,
  ShipmentSchedule,
} from "../../store/slices/tender/TenderList";
import dayjs from "dayjs";

interface ShipmentScheduleRow extends ShipmentSchedule {
  isEditing?: boolean;
}

const EditShipmentScheduleTable: FC<{
  onSave: any;
  list: ShipmentSchedule[];
  lineNo: number;
  onCancel: () => void;
}> = ({ list, onCancel, onSave, lineNo }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{
    status: "success" | "danger" | undefined;
    message: string;
  }>({
    status: undefined,
    message: "",
  });
  const [data, setData] = useState<ShipmentScheduleRow[]>(list || []);
  const [editingKey, setEditingKey] = useState<string | number | null>(null);
  const [originalRow, setOriginalRow] = useState<ShipmentScheduleRow | null>(
    null
  );
  const [initialKeys, setInitialKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setInitialKeys(new Set(list?.map((item) => item.key)));
  }, [list]);

  const isEditing = (record: ShipmentScheduleRow) => record.key === editingKey;

  const handleEdit = (key: string | number) => {
    const row = data.find((item) => item.key === key);
    if (row) {
      setOriginalRow({ ...row });
      setEditingKey(key);
    }
  };

  const handleCancel = (key: string | number) => {
    const isNew = key?.toString()?.startsWith("new-");
    if (isNew && !originalRow?.key) {
      handleDelete(key);
      setOriginalRow(null);
      return;
    }
    setData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, ...originalRow } : item
      )
    );
    setEditingKey(null);
    setOriginalRow(null);
  };

  const handleSave = (key: string | number) => {
    const row = data.find((item) => item.key == key);
    if (!row) return;

    if (!row.shippingLeadDays || !row.shippingQuantity || !row.shippingDate) {
      setAlertMessage({
        status: "danger",
        message: "Please fill all fields before saving.",
      });
      setTimeout(
        () => setAlertMessage({ status: undefined, message: "" }),
        4000
      );
      return;
    }
    setEditingKey(null);
    setOriginalRow(null);
  };

  const handleDelete = (key: string | number) => {
    setData((prev) => prev.filter((item) => item.key !== key));
    if (editingKey === key) {
      setEditingKey(null);
    }
  };

  const handleAdd = () => {
    if (editingKey) return;

    const newKey = `new-${Date.now()}`;
    setData((prev: ShipmentScheduleRow[]) => [
      ...prev,
      {
        key: newKey,
        shippingDate: "",
        shippingEndDate: "",
        shippingQuantity: 0,
        shippingLeadDays: 0,
        isEditing: true,
      },
    ]);
    setEditingKey(newKey);
  };

  const handleFinalSave = () => {
    setIsSaving(true);

    onSave((materials: MaterialOrService[]) => {
      return materials.map((material: any) => {
        if (material?.lineNo == lineNo) {
          return {
            ...material,
            _importError: null,
            shipmentSchedules: data?.map((item) => ({
              key: item?.key,
              shippingQuantity: item?.shippingQuantity,
              shippingLeadDays: item?.shippingLeadDays,
              shippingDate: item?.shippingDate,
              shippingEndDate: item?.shippingEndDate,
            })),
          };
        } else return material;
      });
    });
    onCancel();
  };

  const handleChange = (
    key: string,
    field: keyof ShipmentScheduleRow,
    value: any
  ) => {
    setData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  const configColumns = (data: ShipmentScheduleRow[]) => {
    return [
      {
        title: "Shipping Quantity",
        dataIndex: "shippingQuantity",
        key: "shippingQuantity",
        render: (_: any, record: ShipmentScheduleRow) =>
          isEditing(record) ? (
            <InputNumber
              value={record.shippingQuantity}
              style={{ width: "100%" }}
              step={1}
              parser={(value: any) => parseInt(value, 10)}
              min={-1000}
              max={1000}
              onChange={(value) =>
                handleChange(record.key, "shippingQuantity", value ?? 0)
              }
            />
          ) : (
            record.shippingQuantity
          ),
      },
      {
        title: "Shipping Start Date",
        dataIndex: "shippingDate",
        key: "shippingDate",
        render: (_: any, record: ShipmentScheduleRow) =>
          isEditing(record) ? (
            <DatePicker
              value={record.shippingDate ? dayjs(record.shippingDate) : null}
              format="DD/MMM/YYYY"
              placeholder="Select Date"
              onChange={(date, dateString) =>
                handleChange(record.key, "shippingDate", dateString)
              }
            />
          ) : (
            record.shippingDate
          ),
      },
      {
        title: "Lag Days",
        dataIndex: "lagDays",
        key: "lagDays",
        width: data?.length == 0 ? 120 : 80,
        render: (_: any, record: ShipmentScheduleRow) =>
          isEditing(record) ? (
            <InputNumber
              value={record.shippingLeadDays}
              style={{ width: "100%" }}
              step={1}
              parser={(value: any) => parseInt(value, 10)}
              min={-1000}
              max={1000}
              onChange={(value) =>
                handleChange(record.key, "shippingLeadDays", value ?? 0)
              }
            />
          ) : (
            record.shippingLeadDays
          ),
      },
      {
        title: "Shipping End Date",
        dataIndex: "shippingEndDate",
        key: "shippingEndDate",
        render: (_: any, record: ShipmentScheduleRow) => {
          const shippingEndDate =
            record.shippingDate && record.shippingLeadDays
              ? dayjs(record.shippingDate)
                  .add(record.shippingLeadDays, "days")
                  .format("DD/MMM/YYYY")
              : "";
          return isEditing(record) ? (
            <span>{shippingEndDate}</span>
          ) : (
            shippingEndDate
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        hidden: data?.length == 0,
        width: 100,
        render: (_: any, record: ShipmentScheduleRow) => {
          const editable = isEditing(record);
          return editable ? (
            <Space>
              <Button
                type="button"
                className="btn-dim btn btn-secondary px-2 py-1"
                onClick={() => handleSave(record.key)}
              >
                <SaveOutlined />
              </Button>
              <Button
                type="button"
                className="btn-dim btn btn-secondary px-2 py-1"
                onClick={() => handleCancel(record.key)}
              >
                <CloseOutlined />
              </Button>
            </Space>
          ) : (
            <Space>
              <Button
                className="btn-dim btn btn-secondary px-2 py-1"
                type="button"
                disabled={!!editingKey}
                onClick={() => handleEdit(record.key)}
              >
                <EditOutlined />
              </Button>
              <Button
                onClick={() => handleDelete(record.key)}
                className="btn-dim btn btn-secondary px-2 py-1"
                type="button"
                danger
                disabled={!!editingKey}
              >
                <DeleteOutlined />
              </Button>
            </Space>
          );
        },
      },
    ];
  };

  return (
    <div>
      <Table
        className="customTable"
        columns={configColumns(data)}
        dataSource={data}
        pagination={false}
        bordered
        rowKey="key"
        locale={{
          emptyText: (
            <>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <>
                    <strong>
                      No Schedule Found, Please{" "}
                      <a
                        onClick={handleAdd}
                        className="primary"
                        style={{
                          fontSize: 16,
                          cursor: "pointer",
                        }}
                      >
                        Add Schedule.
                      </a>
                    </strong>
                  </>
                }
              />
            </>
          ),
        }}
      />
      {data?.length != 0 && !alertMessage?.status && (
        <Button
          type="button"
          className="btn-dim btn btn-secondary d-flex align-items-center justify-content-center"
          onClick={handleAdd}
          style={{ marginTop: 16 }}
          block
          disabled={!!editingKey}
        >
          <PlusOutlined />
          &nbsp;
          <span> Add Schedule</span>
        </Button>
      )}
      {!!alertMessage?.status ? (
        <Alert
          isOpen
          color={alertMessage?.status}
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <Icon name={"info"} style={{ top: "0.6rem" }} />
          <ReactTyped
            strings={[alertMessage?.message || ""]}
            typeSpeed={10}
            showCursor={false}
          />
        </Alert>
      ) : (
        <></>
      )}
      {!alertMessage?.status ? (
        <div className="actions clearfix mt-3">
          <ul>
            <li>
              {isSaving ? (
                <Button color="primary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  type="button"
                  disabled={
                    !!editingKey || (list?.length == 0 && data?.length == 0)
                  }
                  onClick={handleFinalSave}
                >
                  Save
                </Button>
              )}
            </li>
            <li>
              <Button
                size="sm"
                color="primary"
                className="btn-dim btn btn-secondary"
                type="button"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </li>
          </ul>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default EditShipmentScheduleTable;
